import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import Avatar from '../components/common/Avatar';
import api from '../utils/api';
import { formatDate } from '../utils/helpers';
import toast from 'react-hot-toast';

const ContributorsPage = () => {
  const [contributors, setContributors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [contributorsRes, statsRes] = await Promise.all([
        api.get('/contributors/leaderboard'),
        api.get('/contributors/stats')
      ]);

      if (contributorsRes.data.success) {
        setContributors(contributorsRes.data.contributors);
      }
      if (statsRes.data.success) {
        setStats(statsRes.data.stats);
      }
    } catch (error) {
      console.error('Erreur chargement contributeurs:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getMedalIcon = (rank) => {
    if (rank === 1) return { icon: '🥇', color: 'text-yellow-400' };
    if (rank === 2) return { icon: '🥈', color: 'text-gray-400' };
    if (rank === 3) return { icon: '🥉', color: 'text-orange-400' };
    return { icon: `#${rank}`, color: 'text-gray-500' };
  };

  return (
    <>
      <Helmet>
        <title>Classement des Contributeurs - Blog SunServ</title>
        <meta name="description" content="Découvrez les meilleurs contributeurs du blog SunServ et leurs statistiques." />
      </Helmet>

      <Header />

      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              🏆 Classement des <span className="text-yellow-400">Contributeurs</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Découvrez les auteurs les plus actifs de notre communauté et leurs contributions
            </p>
          </div>

          {/* Stats globales */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
              <div className="bg-surface rounded-xl p-6 border border-gray-700/30 text-center">
                <i className="fas fa-users text-3xl text-yellow-400 mb-3"></i>
                <p className="text-3xl font-bold text-white">{stats.total_contributors}</p>
                <p className="text-sm text-gray-400">Contributeurs actifs</p>
              </div>
              <div className="bg-surface rounded-xl p-6 border border-gray-700/30 text-center">
                <i className="fas fa-newspaper text-3xl text-blue-400 mb-3"></i>
                <p className="text-3xl font-bold text-white">{stats.total_articles}</p>
                <p className="text-sm text-gray-400">Articles publiés</p>
              </div>
              <div className="bg-surface rounded-xl p-6 border border-gray-700/30 text-center">
                <i className="fas fa-coins text-3xl text-green-400 mb-3"></i>
                <p className="text-3xl font-bold text-white">{parseFloat(stats.total_rewards_paid).toFixed(2)}€</p>
                <p className="text-sm text-gray-400">Récompenses versées</p>
              </div>
              <div className="bg-surface rounded-xl p-6 border border-gray-700/30 text-center">
                <i className="fas fa-chart-line text-3xl text-purple-400 mb-3"></i>
                <p className="text-3xl font-bold text-white">{parseFloat(stats.avg_articles_per_contributor).toFixed(1)}</p>
                <p className="text-sm text-gray-400">Moyenne articles/auteur</p>
              </div>
            </div>
          )}

          {/* Liste des contributeurs */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-400 mt-4">Chargement du classement...</p>
            </div>
          ) : (
            <div className="bg-surface rounded-2xl border border-gray-700/30 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800/50 border-b border-gray-700/30">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Rang</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Contributeur</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Tags</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Articles</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Revenus</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Membre depuis</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/30">
                    {contributors.map((contributor, index) => {
                      const rank = index + 1;
                      const medal = getMedalIcon(rank);

                      return (
                        <tr
                          key={contributor.id}
                          className="hover:bg-gray-800/30 transition-colors"
                        >
                          {/* Rang */}
                          <td className="px-6 py-4">
                            <div className={`text-2xl font-bold ${medal.color}`}>
                              {medal.icon}
                            </div>
                          </td>

                          {/* Contributeur */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-4">
                              <Avatar 
                                user={{
                                  username: contributor.username,
                                  avatar: contributor.avatar
                                }} 
                                size="md"
                              />
                              <div>
                                <p className="font-semibold text-white">
                                  {contributor.display_name || contributor.username}
                                </p>
                                {contributor.bio && (
                                  <p className="text-sm text-gray-400 line-clamp-1 max-w-md">
                                    {contributor.bio}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Tags */}
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-2">
                              {contributor.tags && contributor.tags.length > 0 ? (
                                contributor.tags.map(tag => (
                                  <span
                                    key={tag.id}
                                    className="px-2 py-1 rounded-full text-xs font-medium text-gray-900"
                                    style={{ backgroundColor: tag.color }}
                                  >
                                    {tag.name}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-gray-500">-</span>
                              )}
                            </div>
                          </td>

                          {/* Articles */}
                          <td className="px-6 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <i className="fas fa-newspaper text-blue-400"></i>
                              <span className="text-lg font-bold text-white">
                                {contributor.article_count}
                              </span>
                            </div>
                          </td>

                          {/* Revenus */}
                          <td className="px-6 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <i className="fas fa-coins text-green-400"></i>
                              <span className="text-lg font-bold text-white">
                                {parseFloat(contributor.total_earnings).toFixed(2)}€
                              </span>
                            </div>
                          </td>

                          {/* Date */}
                          <td className="px-6 py-4 text-center">
                            <span className="text-sm text-gray-400">
                              {formatDate(contributor.joined_at)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {contributors.length === 0 && (
                  <div className="text-center py-12">
                    <i className="fas fa-users text-5xl text-gray-600 mb-4"></i>
                    <p className="text-gray-400">Aucun contributeur pour le moment</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 bg-gradient-to-r from-yellow-400/10 to-orange-400/10 rounded-2xl p-8 border border-yellow-400/30 text-center">
            <h2 className="text-2xl font-bold text-white mb-3">
              🚀 Rejoignez notre équipe de contributeurs !
            </h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Partagez vos connaissances, aidez la communauté et gagnez des récompenses pour chaque article publié.
            </p>
            <Link
              to="/login"
              className="inline-block bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-3 rounded-xl font-semibold transition-all hover:scale-105 shadow-lg"
            >
              Devenir contributeur
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ContributorsPage;
